import { LogUserActivity } from '@/app/apis/users/requests'
import { Button } from '@/app/common/components/button/Button'
import Image from '@/app/common/components/Image'
import { LOG_TYPES } from '@/app/common/constants'
import Label from '@/common/components/Label.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { macros } from '@/pages/providers/decision_popup/constants.ts'
import { ProviderQueries, ProviderQueryKeys } from '@/queries/providers/queries'
import {
  providerAcceptReject,
  sendProviderMessage,
} from '@/queries/providers/requests'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog.tsx'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'
import { Textarea } from '@/shad/ui/textarea'
import { toast } from '@/shad/ui/toast/use-toast.ts'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { z } from 'zod'
import {
  REASONING_VALUES,
  ReasoningInputSchema,
} from '../../applicants/applicant_details/hooks/useSubmitFlagDecision'

const StatusSchema = z.enum(['accepted', 'rejected'], {
  message: 'Please select a decision.',
})

const decisionToApiValue = {
  accepted: 4,
  rejected: 5,
} as const

const FormSchema = z.object({
  status: StatusSchema,
  reasoning: ReasoningInputSchema,
  justification: z.string().min(1, { message: 'Internal note is required' }),
  message: z.string().min(1, { message: 'Message is required' }),
})

export type DecisionFormData = z.infer<typeof FormSchema>

const ProviderDecisionPopup = () => {
  const [open, setOpen] = useState(false)

  const { providerId } = useParams()

  if (!providerId) {
    throw new Error('ProviderId Not found')
  }

  const programId = useSelector(selectSelectedProgram).id

  const [selectedMacroTitle, setSelectedMacroTitle] = useState('')
  const {
    control,
    handleSubmit,
    reset,
    register,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<DecisionFormData>({
    resolver: zodResolver(FormSchema),
  })

  const selectedProgram = useSelector(selectSelectedProgram)
  const queryClient = useQueryClient()

  const { data: providerInfo } = useQuery(
    ProviderQueries.getProviderById(programId, providerId)
  )

  const mutation = useMutation({
    mutationFn: async (data: DecisionFormData) => {
      const mappedDecisionValue = decisionToApiValue[data.status]
      const reasoningValue = REASONING_VALUES[data.reasoning] || 0

      const responseProviderStatus = await providerAcceptReject(
        parseInt(providerId, 10),
        programId,
        mappedDecisionValue,
        reasoningValue,
        data.justification,
        data.message
      )

      const responseProviderMessage = await sendProviderMessage(
        parseInt(providerId ?? '0', 10),
        data.message
      )

      return { responseProviderStatus, responseProviderMessage }
    },
    onSuccess: ({ responseProviderStatus, responseProviderMessage }) => {
      if (
        responseProviderStatus.success &&
        responseProviderMessage.message === 'Message posted successfully.'
      ) {
        toast({
          variant: 'default',
          description: responseProviderStatus.message,
        })
        queryClient.invalidateQueries({
          queryKey: ProviderQueryKeys.details(selectedProgram.id, providerId),
        })
        setOpen(false)
        reset()
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: responseProviderStatus.message,
        })

        setOpen(false)
      }
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'An error occurred while submitting the form.',
      })
    },
  })

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current && open) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'review of Provider review page',
      })
      hasLoggedPageView.current = true
    }
  }, [open])

  const handleOnSubmit = (data: DecisionFormData) => {
    mutation.mutate(data)
  }

  watch('reasoning')

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          reset()
          setSelectedMacroTitle('')
        }
      }}
    >
      <DialogTrigger asChild>
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm">
            Review
          </Button>
        </div>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Decision</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  className="space-y-2"
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="accepted" id="accept" />
                    <Label weight="normal" htmlFor="accept">
                      I <span className="text-green-700">accept</span> the
                      notification made by the provider
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="rejected" id="reject" />
                    <Label weight="normal" htmlFor="reject">
                      I <span className="text-red-700">reject</span> the
                      notification made by the provider
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.status && (
              <p className="text-sm text-red-500">{errors.status.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Reasoning</Label>
            <Select
              value={getValues().reasoning}
              onValueChange={(value) => {
                const reasoning = ReasoningInputSchema.parse(value)
                setValue('reasoning', reasoning)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select reasoning" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={'Based on evidence'}>
                  Based on evidence
                </SelectItem>
                <SelectItem value={'Based on program rules'}>
                  Based on program rules
                </SelectItem>
                <SelectItem value={'Other'}>Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.reasoning && (
              <p className="text-sm text-red-500">{errors.reasoning.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Internal Note</Label>
            <Textarea
              {...register('justification')}
              placeholder="Enter your internal note"
              rows={2}
            />
            {errors.justification && (
              <p className="text-sm text-red-500">
                {errors.justification.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Macros</Label>
            <Select
              value={selectedMacroTitle}
              onValueChange={(value) => {
                setSelectedMacroTitle(value)
                const selectedMacro = macros.find(
                  (macro) => macro.title === value
                )
                if (selectedMacro) {
                  setValue('message', selectedMacro.body || '')
                }
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a macro" />
              </SelectTrigger>
              <SelectContent>
                {macros.map((macro, index) => (
                  <SelectItem key={index} value={macro.title}>
                    {macro.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Public Reply</Label>
            <Textarea
              {...register('message')}
              placeholder="Enter your reply"
              rows={6}
            />
            {errors.message && (
              <p className="text-sm text-red-500">{errors.message.message}</p>
            )}
          </div>

          <div className="flex gap-2">
            <SubmitButton
              status={mutation.isPending ? 'pending' : 'idle'}
              className=""
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
          </div>

          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-5 mt-2 sm:mt-8 max-h-16 sm:max-h-48 overflow-auto">
            {providerInfo?.files &&
              providerInfo?.files?.map((document, index) => {
                return (
                  <Image
                    key={index}
                    className="shadow-lg"
                    allowZoom={true}
                    path={document.path ?? ''}
                    width={'100%'}
                    alt={''}
                    widthIframe={'100%'}
                    heightIframe={'100%'}
                  />
                )
              })}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default ProviderDecisionPopup
